import {useAuth} from '../use-auth';
import {ReactElement, useContext, useEffect} from 'react';
import {Navigate, Outlet, useLocation} from 'react-router-dom';
import {useQuery} from '@tanstack/react-query';
import {useAppearanceEditorMode} from '../../admin/appearance/commands/use-appearance-editor-mode';
import {SiteConfigContext} from '@common/core/settings/site-config-context';
import {apiClient} from '@common/http/query-client';
import {BootstrapData} from '@common/core/bootstrap-data/bootstrap-data';
import {useBootstrapData} from '@common/core/bootstrap-data/bootstrap-data-context';
import {FullPageLoader} from '@common/ui/progress/full-page-loader';

interface GuestRouteProps {
  children: ReactElement;
}
export function GuestRoute({children}: GuestRouteProps) {
  const {isLoggedIn, getRedirectUri} = useAuth();
  const {isAppearanceEditorActive} = useAppearanceEditorMode();
  const {setBootstrapData} = useBootstrapData();
  const {auth} = useContext(SiteConfigContext);
  const {pathname} = useLocation();
  // Recheck the server session when opening an auth page or returning from
  // another tab. The existing bootstrap snapshot can still describe a guest.
  const session = useQuery({
    queryKey: ['guest-route-session'],
    enabled: !isLoggedIn && !isAppearanceEditorActive,
    queryFn: async ({signal}) => {
      const response = await apiClient.get<{data: string | BootstrapData}>('bootstrap-data', {signal});
      return typeof response.data.data === 'string'
        ? JSON.parse(response.data.data) as BootstrapData
        : response.data.data;
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    retry: false,
  });
  useEffect(() => {
    if (!isLoggedIn && session.data?.user) setBootstrapData(session.data);
  }, [isLoggedIn, session.data, setBootstrapData]);

  if (isLoggedIn && !isAppearanceEditorActive) {
    const redirectUri = getRedirectUri();
    if (redirectUri !== pathname) return <Navigate to={redirectUri} replace />;
    return <Navigate to={auth.secondaryRedirectUri || '/dashboard'} replace />;
  }
  if (!isAppearanceEditorActive && (session.isPending || session.data?.user)) {
    return <FullPageLoader screen />;
  }
  return children || <Outlet />;
}
